import { dispatchTrackingEvent } from '@context/tracking/utils';
import { ProductEventWither } from '@helpers/googleTagManager';

import { ReviewableProductWithUrl } from './useReviewableProducts';

/**
 * Which variant of the "review sent" screen the customer sees. Reported as
 * `subpage.name` so the funnel can be split by what the screen offered next.
 */
export type ReviewSubmitVariant =
  | 'review_submit_blank'
  | 'review_submit_products';

const REVIEW_SUBMIT_TITLE = 'review_submit';

/** GA4 item list the reviewable products are reported under. */
const REVIEWABLE_PRODUCTS_LIST_NAME = 'reviewable_products';

export const trackReviewSubmitView = ({
  variant,
  timing,
}: {
  variant: ReviewSubmitVariant;
  timing: number;
}) =>
  dispatchTrackingEvent({
    event: 'subpage_view',
    subpage: {
      name: variant,
      type: 'review',
      timing,
      title: REVIEW_SUBMIT_TITLE,
    },
    _clear: true,
  });

/**
 * Clears the `subpage` data from the dataLayer so it is not attributed to
 * whatever the customer does next.
 */
export const trackReviewSubmitClose = () =>
  dispatchTrackingEvent({
    event: 'subpage_close',
    subpage: undefined,
    _clear: true,
  });

export const trackMyReviewsClick = ({ timing }: { timing: number }) =>
  dispatchTrackingEvent({
    event: 'element_click',
    element: {
      interaction: 'click',
      name: 'my_reviews',
      timing,
      type: REVIEW_SUBMIT_TITLE,
    },
    _clear: true,
  });

/**
 * The customer left the screen for one of the offered products, so it is
 * reported as picking an item out of the `reviewable_products` list.
 *
 * Per the tracking spec this event carries no `promo_labels`, `discount_price`,
 * `isSponsored` or `campaign_ids` – which is why the attributes those are
 * derived from are not part of the query and are stubbed out for the wither.
 */
export const trackReviewableProductSelect = ({
  product,
  position,
}: {
  product: ReviewableProductWithUrl['product'];
  position: number;
}) =>
  dispatchTrackingEvent({
    event: 'select_item',
    product: [
      ProductEventWither()
        .withVpProduct({ ...product, attributes: {} } as Parameters<
          ReturnType<typeof ProductEventWither>['withVpProduct']
        >[0])
        .withAdditionalData({
          promo_labels: undefined,
          list_name: REVIEWABLE_PRODUCTS_LIST_NAME,
          list_position: position,
        })
        .build(),
    ],
    _clear: true,
  });

export const trackWriteReviewClick = ({ timing }: { timing: number }) =>
  dispatchTrackingEvent({
    event: 'element_click',
    element: {
      action: 'wt_add_review',
      interaction: 'click',
      mode: 'open',
      name: 'review',
      timing,
      type: REVIEW_SUBMIT_TITLE,
    },
    _clear: true,
  });
